import React from "react";
import {
  AbsoluteFill, useCurrentFrame, useVideoConfig,
  spring, interpolate, Audio, staticFile,
} from "remotion";
import {z} from "zod";
import {TransitionSeries, linearTiming} from "@remotion/transitions";
// import {wipe} from "@remotion/transitions/wipe"; // reserved for future use
import {fade} from "@remotion/transitions/fade";
import {slide} from "@remotion/transitions/slide";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {SceneBackground} from "../components/backgrounds/SceneBackground";
import {KineticText} from "../components/text/KineticText";
import {GlowText} from "../components/dimus/GlowText";
import {AccentLine} from "../components/dimus/AccentLine";
import {AudioTrack} from "../components/media/AudioTrack";
import {SERIES_BGM, scenesToDuckSegments} from "../presets/music";

/**
 * VerdadesReel — Template parametrizável para linha "verdades_dificeis"
 *
 * Visual DNA: cinematic, pesado, direto. Estilo editorial escuro.
 * Accent: vermelho (#F87171). Fundo: gradient-drift / particles-dense / dark-void.
 * Tom: "verdade que dói mas precisa ser dita".
 */

const ACCENT  = IDV.error;    // vermelho
const WHITE   = "#f5f5f7";
const MUTED   = "#a3a3a3";
const SZ      = SAFE_ZONES.universal;
const TRANS   = 18;

const baseScene = {
  audio: z.string(), durFrames: z.number(), pill: z.string().optional(),
};

export const verdadesSceneSchema = z.discriminatedUnion("type", [
  z.object({ ...baseScene, type: z.literal("hook"),
    title: z.string(), highlightWords: z.array(z.string()).optional() }),

  z.object({ ...baseScene, type: z.literal("truth"),
    title: z.string(), highlightWords: z.array(z.string()).optional(),
    evidence: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("stat"),
    stat: z.string(), statLabel: z.string(), subtext: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("contrast"),
    falseStatement: z.string(), trueStatement: z.string() }),

  z.object({ ...baseScene, type: z.literal("list"),
    title: z.string(), highlightWords: z.array(z.string()).optional(),
    bullets: z.array(z.string()) }),

  z.object({ ...baseScene, type: z.literal("provocation"),
    question: z.string(), hint: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("cta"),
    question: z.string() }),
]);

export type VerdadesScene = z.infer<typeof verdadesSceneSchema>;

export const verdadesReelSchema = z.object({
  scenes: z.array(verdadesSceneSchema).min(1),
  bgm: z.string().default("music/cafe-lofi.mp3"),
  bgmVolume: z.number().default(0.10),
  ttsVolume: z.number().default(0.95),
});

// ── Pill ──────────────────────────────────────────────────────────────────────
const Pill: React.FC<{text: string; delay?: number}> = ({text, delay = 0}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p, transform: `scale(${interpolate(p,[0,1],[0.7,1],CLAMP)})`,
      padding: "10px 22px", borderRadius: 999, display:"flex", alignItems:"center", gap: 8,
      background: `${ACCENT}18`, border: `1.5px solid ${ACCENT}44`,
    }}>
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color: ACCENT, fontFamily: FONT_STACK, letterSpacing: 1.5}}>{text}</span>
    </div>
  );
};

const BulletItem: React.FC<{text: string; delay: number}> = ({text, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{display:"flex", alignItems:"flex-start", gap: 16, opacity: p,
      transform: `translateX(${interpolate(p,[0,1],[-20,0],CLAMP)}px)`}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:ACCENT,marginTop:12,flexShrink:0,boxShadow:`0 0 8px ${ACCENT}99`}}/>
      <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: FONT_SCALE.body.min, fontWeight: 600, lineHeight: 1.3}}>{text}</div>
    </div>
  );
};

// ── Scenes ───────────────────────────────────────────────────────────────────
const SceneHook: React.FC<{s:Extract<VerdadesScene,{type:"hook"}>;tv:number}> = ({s,tv}) => (
  <AbsoluteFill>
    <SceneBackground mode="particles-dense" accent={ACCENT} accent2={IDV.primary}/>
    <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
      gap:24,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(s.audio)} volume={tv}/>
      <Pill text={s.pill ?? "VERDADES DIFÍCEIS"} delay={5}/>
      <KineticText
        text={s.title} mode="stagger-blur" delay={15}
        fontSize={FONT_SCALE.h1.max} fontWeight={900} color={WHITE}
        highlightWords={s.highlightWords??[]} highlightColor={ACCENT}
        textAlign="center" lineHeight={1.15} maxWidth="90%"
      />
      <AccentLine color={ACCENT} delay={45} width={160}/>
    </AbsoluteFill>
  </AbsoluteFill>
);

const SceneTruth: React.FC<{s:Extract<VerdadesScene,{type:"truth"}>;tv:number}> = ({s,tv}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const evP = spring({frame: frame - 70, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" accent={ACCENT} accent2={IDV.primary}/>
      <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
        gap:28,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={tv}/>
        {s.pill && <Pill text={s.pill} delay={5}/>}
        <KineticText
          text={s.title} mode="stagger-up" delay={15}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightWords={s.highlightWords??[]} highlightColor={ACCENT}
          textAlign="center" lineHeight={1.2} maxWidth="92%"
        />
        {s.evidence && (
          <div style={{opacity: evP, transform:`translateY(${interpolate(evP,[0,1],[16,0],CLAMP)}px)`,
            padding:"20px 28px",borderRadius:14,background:`${ACCENT}10`,border:`1.5px solid ${ACCENT}35`}}>
            <div style={{fontFamily:FONT_STACK,color:MUTED,fontSize:FONT_SCALE.body.min,fontWeight:500,lineHeight:1.4,textAlign:"center"}}>{s.evidence}</div>
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneStat: React.FC<{s:Extract<VerdadesScene,{type:"stat"}>;tv:number}> = ({s,tv}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const lP = spring({frame:frame-40,fps,config:SPRINGS.smooth});
  const sP = spring({frame:frame-80,fps,config:SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" accent={ACCENT}/>
      <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
        gap:16,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={tv}/>
        <GlowText text={s.stat} delay={0} size={FONT_SCALE.hero.max} color={ACCENT}/>
        <div style={{opacity:lP,fontFamily:FONT_STACK,color:MUTED,letterSpacing:3,
          fontSize:FONT_SCALE.label.min,fontWeight:700,textAlign:"center"}}>{s.statLabel}</div>
        {s.subtext && <div style={{opacity:sP}}>
          <KineticText text={s.subtext} mode="wave" delay={90} fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED} textAlign="center"/>
        </div>}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneContrast: React.FC<{s:Extract<VerdadesScene,{type:"contrast"}>;tv:number}> = ({s,tv}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const falseP = spring({frame:frame-5,fps,config:SPRINGS.heavy});
  const trueP = spring({frame:frame-60,fps,config:SPRINGS.heavy});
  return (
    <AbsoluteFill>
      <SceneBackground mode="split-tone" accent={IDV.error} accent2={IDV.secondary}/>
      <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
        gap:32,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={tv}/>
        {/* FALSO */}
        <div style={{opacity:falseP,transform:`translateX(${interpolate(falseP,[0,1],[-30,0],CLAMP)}px)`,
          padding:"20px 28px",borderRadius:14,background:"rgba(248,113,113,0.08)",
          border:"2px solid rgba(248,113,113,0.25)",width:"90%"}}>
          <div style={{fontFamily:FONT_STACK,color:IDV.error,fontSize:FONT_SCALE.label.min,fontWeight:700,letterSpacing:2,marginBottom:8}}>❌ MITO</div>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:FONT_SCALE.body.min,fontWeight:600,lineHeight:1.3}}>{s.falseStatement}</div>
        </div>
        {/* VERDADE */}
        <div style={{opacity:trueP,transform:`translateX(${interpolate(trueP,[0,1],[30,0],CLAMP)}px)`,
          padding:"20px 28px",borderRadius:14,background:"rgba(0,223,130,0.08)",
          border:"2px solid rgba(0,223,130,0.35)",width:"90%",boxShadow:`0 0 24px ${IDV.secondary}20`}}>
          <div style={{fontFamily:FONT_STACK,color:IDV.secondary,fontSize:FONT_SCALE.label.min,fontWeight:700,letterSpacing:2,marginBottom:8}}>✅ VERDADE</div>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:FONT_SCALE.body.min,fontWeight:600,lineHeight:1.3}}>{s.trueStatement}</div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneList: React.FC<{s:Extract<VerdadesScene,{type:"list"}>;tv:number}> = ({s,tv}) => (
  <AbsoluteFill>
    <SceneBackground mode="dark-void" accent={ACCENT}/>
    <AbsoluteFill style={{justifyContent:"center",alignItems:"flex-start",flexDirection:"column",
      gap:28,padding:`${SZ.top}px ${SZ.left+16}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(s.audio)} volume={tv}/>
      {s.pill && <Pill text={s.pill} delay={5}/>}
      <KineticText text={s.title} mode="stagger-up" delay={15}
        fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
        highlightWords={s.highlightWords??[]} highlightColor={ACCENT}
        textAlign="left" lineHeight={1.2} maxWidth="95%"/>
      <div style={{display:"flex",flexDirection:"column",gap:20,width:"100%"}}>
        {s.bullets.map((b,i)=><BulletItem key={i} text={b} delay={30+i*20}/>)}
      </div>
    </AbsoluteFill>
  </AbsoluteFill>
);

const SceneProvocation: React.FC<{s:Extract<VerdadesScene,{type:"provocation"}>;tv:number}> = ({s,tv}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const hintP = spring({frame:frame-80,fps,config:SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="floating-shapes" accent={ACCENT} accent2={IDV.primary} seed="v-prov"/>
      <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
        gap:28,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={tv}/>
        <KineticText text={s.question} mode="split-reveal" delay={10}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightColor={ACCENT} textAlign="center" lineHeight={1.2} maxWidth="90%"/>
        {s.hint && <div style={{opacity:hintP,transform:`translateY(${interpolate(hintP,[0,1],[12,0],CLAMP)}px)`}}>
          <KineticText text={s.hint} mode="stagger-blur" delay={85}
            fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED} textAlign="center"/>
        </div>}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneCta: React.FC<{s:Extract<VerdadesScene,{type:"cta"}>;tv:number}> = ({s,tv}) => (
  <AbsoluteFill>
    <SceneBackground mode="gradient-drift" accent={ACCENT} accent2={IDV.primary}/>
    <AbsoluteFill style={{justifyContent:"center",alignItems:"center",flexDirection:"column",
      gap:28,padding:`${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(s.audio)} volume={tv}/>
      <KineticText text={s.question} mode="split-reveal" delay={10}
        fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
        highlightColor={ACCENT} textAlign="center" lineHeight={1.2} maxWidth="90%"/>
      <AccentLine color={ACCENT} delay={80} width={120}/>
    </AbsoluteFill>
  </AbsoluteFill>
);

const TRANS_LIST = [fade, fade, slide, fade, fade] as const;

export const VerdadesReel: React.FC<z.infer<typeof verdadesReelSchema>> = ({scenes, bgm, bgmVolume, ttsVolume}) => {
  const bgmConf = SERIES_BGM["cafe-com-teu-roi"];
  const duckSegs = scenesToDuckSegments(scenes.map(s=>({dur:s.durFrames,file:s.audio})), 30);

  const render = (s: VerdadesScene, i: number) => {
    switch(s.type){
      case "hook":        return <SceneHook s={s} tv={ttsVolume} key={i}/>;
      case "truth":       return <SceneTruth s={s} tv={ttsVolume} key={i}/>;
      case "stat":        return <SceneStat s={s} tv={ttsVolume} key={i}/>;
      case "contrast":    return <SceneContrast s={s} tv={ttsVolume} key={i}/>;
      case "list":        return <SceneList s={s} tv={ttsVolume} key={i}/>;
      case "provocation": return <SceneProvocation s={s} tv={ttsVolume} key={i}/>;
      case "cta":         return <SceneCta s={s} tv={ttsVolume} key={i}/>;
    }
  };

  return (
    <AbsoluteFill style={{backgroundColor:"#000"}}>
      <AudioTrack src={staticFile(bgm)} volume={bgmVolume}
        duckDuringSegments={duckSegs} duckVolume={bgmConf.duckVolume}
        fadeInDurationSeconds={bgmConf.fadeInSeconds}
        fadeOutDurationSeconds={bgmConf.fadeOutSeconds} loop/>
      <TransitionSeries>
        {scenes.map((s,i)=>(
          <React.Fragment key={i}>
            <TransitionSeries.Sequence durationInFrames={s.durFrames}>
              {render(s,i)}
            </TransitionSeries.Sequence>
            {i < scenes.length-1 && (
              <TransitionSeries.Transition
                presentation={TRANS_LIST[i % TRANS_LIST.length]()}
                timing={linearTiming({durationInFrames: TRANS})}/>
            )}
          </React.Fragment>
        ))}
      </TransitionSeries>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
